import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, ArrowLeft, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Swoosh } from "@/components/Brand";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const STATUS_COLORS = {
  pending: "text-yellow-400 border-yellow-400/40",
  confirmed: "text-lime border-lime/40",
  completed: "text-white/50 border-white/20",
  cancelled: "text-red-400 border-red-400/40",
};

function Login({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("adl_token", data.access_token);
      toast.success("Welcome back.");
      onAuthed();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-ink">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md border border-white/10 bg-surface p-10"
      >
        <div className="flex items-center gap-3 mb-10">
          <Swoosh className="w-9 h-9 text-lime" />
          <span className="font-display font-semibold tracking-tight">
            AERO DETAIL <span className="text-purple">LAB</span>
          </span>
        </div>
        <span className="overline">[ Admin access ]</span>
        <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight">Booking Console</h1>
        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="admin-login-form">
          <div>
            <label className="overline block mb-2">Email</label>
            <input data-testid="admin-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="clinical-input" placeholder="admin@aerodetaillab.com" />
          </div>
          <div>
            <label className="overline block mb-2">Password</label>
            <input data-testid="admin-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="clinical-input" placeholder="••••••••" />
          </div>
          <button data-testid="admin-login-btn" disabled={loading} className="btn-fill w-full border border-lime text-lime py-3.5 font-mono text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-50">
            {loading && <Loader2 size={15} className="animate-spin" />} Sign In
          </button>
        </form>
        <Link to="/" className="mt-8 inline-flex items-center gap-2 font-mono text-xs text-white/40 hover:text-lime transition-colors uppercase tracking-[0.15em]" data-testid="admin-back-home">
          <ArrowLeft size={13} /> Back to site
        </Link>
      </motion.div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Failed to load bookings.");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/bookings/${id}`, { status });
      setBookings((b) => b.map((x) => (x.id === id ? data : x)));
      toast.success(`Marked ${status}.`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      setBookings((b) => b.filter((x) => x.id !== id));
      toast.success("Booking deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const shown = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: bookings.filter((b) => b.status === s).length }), {});

  return (
    <div className="min-h-screen bg-ink">
      <header className="border-b border-white/10 bg-ink/70 backdrop-blur-xl sticky top-0 z-10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo">
            <Swoosh className="w-8 h-8 text-lime" />
            <span className="font-display font-semibold tracking-tight">Booking Console</span>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh" className="p-2.5 border border-white/15 text-white/70 hover:text-lime hover:border-lime/40 transition-colors">
              <RefreshCw size={15} />
            </button>
            <button onClick={onLogout} data-testid="admin-logout" className="btn-fill border border-white/20 text-white px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-2">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
          {STATUSES.map((s) => (
            <div key={s} className="bg-surface p-6">
              <p className="font-mono text-3xl font-medium text-white">{counts[s] || 0}</p>
              <p className="overline mt-2">{s}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {["all", ...STATUSES].map((f) => (
            <button
              key={f}
              data-testid={`admin-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] border transition-colors ${
                filter === f ? "border-lime text-ink bg-lime" : "border-white/15 text-white/60 hover:border-white/40"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32 text-white/50"><Loader2 className="animate-spin" /></div>
        ) : shown.length === 0 ? (
          <div className="border border-dashed border-white/15 py-32 text-center text-white/40 font-mono text-sm" data-testid="admin-empty">
            No bookings {filter !== "all" ? `with status "${filter}"` : "yet"}.
          </div>
        ) : (
          <div className="border-t border-white/10" data-testid="admin-bookings-list">
            {shown.map((b) => (
              <div key={b.id} data-testid={`admin-booking-${b.id}`} className="grid md:grid-cols-[1.4fr_1fr_1fr_auto] gap-4 md:gap-8 items-start md:items-center py-6 border-b border-white/10 hover:bg-surface/50 transition-colors px-2 md:px-4">
                <div>
                  <p className="font-display text-lg text-white">{b.name}</p>
                  <p className="font-mono text-xs text-white/45">{b.email} · {b.phone}</p>
                  {b.message && <p className="mt-1 text-sm text-white/50 font-body">{b.message}</p>}
                </div>
                <div>
                  <p className="font-mono text-sm text-lime">{b.package}</p>
                  {b.aircraft && <p className="font-mono text-xs text-white/45">{b.aircraft}</p>}
                </div>
                <div className="font-mono text-xs text-white/60">
                  <p>Requested: {b.preferred_date}</p>
                  <span className={`inline-block mt-2 px-2 py-0.5 border text-[0.6rem] uppercase tracking-[0.15em] ${STATUS_COLORS[b.status]}`}>{b.status}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                    <SelectTrigger data-testid={`admin-status-${b.id}`} className="w-[130px] rounded-none bg-transparent border border-white/15 text-white text-xs h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-white/10 text-white rounded-none">
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s} className="font-mono text-xs capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button onClick={() => remove(b.id)} data-testid={`admin-delete-${b.id}`} className="p-2.5 border border-white/15 text-white/50 hover:text-red-400 hover:border-red-400/40 transition-colors">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);

  const check = useCallback(async () => {
    if (!localStorage.getItem("adl_token")) return setAuthed(false);
    try {
      await api.get("/auth/me");
      setAuthed(true);
    } catch {
      localStorage.removeItem("adl_token");
      setAuthed(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const logout = () => {
    localStorage.removeItem("adl_token");
    setAuthed(false);
  };

  if (authed === null) return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="animate-spin text-lime" /></div>;
  return authed ? <Dashboard onLogout={logout} /> : <Login onAuthed={() => setAuthed(true)} />;
}
